import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { BUSINESS_PHONE } from "@/lib/business";

export default function FAQ() {
  const faqs = [
    {
      category: "General Questions",
      questions: [
        {
          q: "What types of inspections do you provide?",
          a: "We provide visual property inspections including Pre-Sale Property Inspections, Building Status Reports, Building Condition Reports, Pool Reports, and Plumbing Reports. For electrical inspections and pest/termite reports, we work with trusted partners."
        },
        {
          q: "How long does an inspection take?",
          a: "A typical inspection takes 2-3 hours for an average-sized property. We focus on thorough visual assessment of all accessible areas."
        },
        {
          q: "When will I receive my report?",
          a: "All reports are delivered within 72 hours of inspection. This fast turnaround keeps your property timeline moving forward."
        },
        {
          q: "What's your pricing structure?",
          a: "We offer flat-rate pricing with no surprises: pool remediation reports from $260, pre-sale and building status reports from $325, and pre-purchase building inspections from $455, all incl. GST. Standard pricing applies to Darwin and Greater NT properties."
        }
      ]
    },
    {
      category: "Reports & Inspections",
      questions: [
        {
          q: "What's included in your reports?",
          a: "Our digital reports include photos and clear documentation of visible issues. We focus on what can be seen \u2014 no structural opinions or grey areas. Reports are AS4349.1-aligned where applicable."
        },
        {
          q: "Do building condition reports include remediation estimates?",
          a: "Yes! All building condition reports automatically include our remediation report at no extra cost. This provides estimated costs to address identified issues."
        },
        {
          q: "What does 'visible issues only' mean?",
          a: "We inspect what can be seen during a visual assessment. We don't provide structural opinions, invasive testing, or assumptions about hidden issues. This keeps reports clear and straightforward."
        },
        {
          q: "Can I use your reports for property transactions?",
          a: "Absolutely. Our reports are designed to support property transactions, reduce renegotiations, and provide buyer confidence. They're commonly used by sellers, buyers, and real estate agents."
        }
      ]
    },
    {
      category: "Pest & Electrical Inspections",
      questions: [
        {
          q: "Do you provide pest and termite inspections?",
          a: "We work with trusted partners to deliver quality insured pest and termite reports. Our partners are fully qualified and experienced in NT pest control."
        },
        {
          q: "What about electrical inspections?",
          a: "Electrical inspections are provided through our trusted partner network. We coordinate full scope electrical reports delivered by qualified electricians."
        },
        {
          q: "Can I arrange pest or electrical inspections through you?",
          a: "Yes! Contact us and we'll coordinate with our trusted partners to arrange your pest or electrical inspection. We ensure smooth scheduling and delivery."
        },
        {
          q: "Are your partners insured?",
          a: "Absolutely. All our partners are fully insured and qualified professionals providing quality reports you can rely on."
        }
      ]
    },
    {
      category: "Pricing & Payment",
      questions: [
        {
          q: "How much does an inspection cost?",
          a: "Pre-purchase building inspections start from $455 (condition and remediation reports included), pest inspections from $325, and pool remediation reports from $260. Final pricing depends on property size, location, and age. Contact us for a free quote."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept bank transfer, credit card, and direct debit. Payment is required before or immediately after the inspection, with reports released upon payment confirmation."
        },
        {
          q: "Are there any travel fees?",
          a: "We service Darwin, Palmerston, and surrounding areas. Properties outside these areas may incur a small travel fee, which we'll discuss when booking."
        },
        {
          q: "Do you offer discounts for combined inspections?",
          a: "Yes! We offer package discounts when you book multiple services together, such as a combined building and pest inspection."
        }
      ]
    },
    {
      category: "Darwin-Specific Questions",
      questions: [
        {
          q: "Who are the best building inspectors in Darwin?",
          a: "Aussie Inspections is a trusted local building inspection company serving Darwin, Palmerston, and the greater NT region. Our team of qualified inspectors has over 15 years combined experience with Darwin's unique tropical building challenges, including moisture damage, termite activity, and cyclone-related wear."
        },
        {
          q: "How much does a building inspection cost in Darwin NT?",
          a: "Inspections in Darwin start from $260 incl. GST with flat-rate pricing. Pre-purchase building inspections start from $455, pest inspections from $325, and pool remediation reports from $260. Final pricing depends on property size, location, and age. We service Darwin, Palmerston, Litchfield, and surrounding areas."
        },
        {
          q: "Do I need a building inspection in the Northern Territory?",
          a: "While not legally required in the NT, a building inspection is highly recommended before purchasing property. Darwin's tropical climate creates unique risks like termite damage, moisture issues, and cyclone wear that may not be visible during a casual viewing. Our AS4349.1-aligned reports help buyers make informed decisions and avoid costly surprises."
        },
        {
          q: "What should I look for in a Darwin property inspection?",
          a: "Darwin properties require specialist knowledge of tropical building challenges: termite and pest activity (extremely common), moisture damage from high humidity, roof deterioration from cyclone seasons, rust on metal fixtures, and drainage issues during wet season. Our inspectors are experienced in identifying these Darwin-specific issues."
        },
        {
          q: "How quickly can I get a building inspection report in Darwin?",
          a: "Aussie Inspections delivers all reports within 72 hours of inspection. This fast turnaround is essential for Darwin's competitive property market, keeping your transaction timeline moving forward. We provide digital reports with photos and clear documentation of all visible issues."
        }
      ]
    },
    {
      category: "Pool & Plumbing Reports",
      questions: [
        {
          q: "What's included in a pool report?",
          a: "Our pool reports provide visual inspection of your pool barriers, gates, and fencing to help you understand if your pool will meet Government requirements. This helps you pass official inspections first time."
        },
        {
          q: "Will your pool report guarantee I pass the official inspection?",
          a: "While we can't guarantee official outcomes, our reports identify areas that may not comply and provide guidance on what needs addressing before the official inspection."
        },
        {
          q: "What does a plumbing report cover?",
          a: "We conduct a full visual inspection of all accessible plumbing systems, including water supply, drainage, and fixtures. The report documents visible issues with photos."
        },
        {
          q: "Do you perform invasive plumbing tests?",
          a: "No, we focus on visual inspection. Our reports document what can be seen \u2014 we don't perform invasive testing or provide technical measurements."
        }
      ]
    }
  ];

  // Generate FAQPage schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap((category) =>
      category.questions.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    )
  };

  return (
    <div>
      <SEO
        title="Frequently Asked Questions"
        description="Common questions about building and property inspections in Darwin NT. Get answers about inspection types, pricing, timing, and processes."
        keywords="building inspection FAQ Darwin, inspection questions NT, property inspection answers, inspection process Darwin"
        url="/faq"
        schema={faqSchema}
      />

      {/* CTA Banner */}
      <CTABanner />

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-chivo text-5xl md:text-6xl font-black tracking-tight leading-none mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Everything you need to know about our inspection services
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqs.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="mb-12"
            >
              <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-6 pb-4 border-b-2 border-primary">
                {category.category}
              </h2>

              <Accordion type="single" collapsible className="w-full">
                {category.questions.map((faq, index) => (
                  <AccordionItem key={index} value={`${categoryIndex}-${index}`}>
                    <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-primary">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 text-base leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Our friendly team is here to help. Get in touch today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
              data-testid="faq-contact-button"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-primary/20 hover:border-primary hover:bg-primary/5 font-bold tracking-wide uppercase px-12"
            >
              <a href={`tel:${BUSINESS_PHONE}`}>Call Now</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
